import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { PrimaryButton, SecondaryButton } from "./AdminUI";
import api from "@/lib/api";
import { toast } from "@/lib/toast";
import { Check, X } from "lucide-react";

/**
 * Modal that asks the editor for a few pointers, generates a short
 * editorial "Our take" via Claude, and lets them Approve (write to form)
 * or Discard.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (bool) => void
 *  - brandId: string
 *  - currentText: string         // existing our_take (shown as "Current")
 *  - onApprove: (text) => void   // called when admin clicks Approve
 */
export function OurTakeGenerateModal({ open, onOpenChange, brandId, currentText, onApprove }) {
  const [pointers, setPointers] = useState("");
  const [generating, setGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState("");

  async function generate() {
    setGenerating(true);
    const t = toast.loading("Generating editorial take with AI…");
    try {
      const res = await api.post(`/admin/brands/${brandId}/generate-our-take`, { pointers });
      const text = (res.data?.our_take || "").trim();
      if (!text) {
        toast.error("LLM returned an empty take", { id: t });
        return;
      }
      setSuggestion(text);
      toast.dismiss(t);
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Generation failed", { id: t });
    } finally {
      setGenerating(false);
    }
  }

  function approve() {
    onApprove(suggestion);
    onOpenChange(false);
    setSuggestion("");
    setPointers("");
  }

  function discard() {
    setSuggestion("");
  }

  function close() {
    setSuggestion("");
    setPointers("");
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="our-take-generate-modal">
        <DialogHeader>
          <DialogTitle>Generate &ldquo;Our take&rdquo; with AI</DialogTitle>
          <DialogDescription>
            Add any pointers you want the editorial to follow — angle, tone, things to highlight or caveat.
            We&apos;ll ground the text on the brand&apos;s claims and warnings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <textarea
            value={pointers}
            onChange={(e) => setPointers(e.target.value)}
            rows={4}
            placeholder="e.g. mention their organic-transition program, caveat the deforestation reports, neutral tone, end with a sentence on why we still feature them."
            className="w-full border border-[#E9ECEF] rounded p-2 text-sm font-admin"
            data-testid="our-take-pointers-input"
          />
          <div className="flex justify-end">
            <PrimaryButton onClick={generate} disabled={generating} data-testid="our-take-generate-button">
              {generating ? "Generating…" : (suggestion ? "Regenerate" : "Generate")}
            </PrimaryButton>
          </div>

          {suggestion && (
            <div className="border border-[#E9ECEF] rounded-md p-3 bg-white space-y-3" data-testid="our-take-suggestion">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Current</div>
                  <div className="text-sm whitespace-pre-wrap">{currentText || <span className="italic text-[#6C757D]">empty</span>}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Suggested (editable)</div>
                  <textarea
                    value={suggestion}
                    onChange={(e) => setSuggestion(e.target.value)}
                    rows={8}
                    className="w-full border border-[#E9ECEF] rounded p-2 text-sm font-admin"
                    data-testid="our-take-suggestion-editor"
                  />
                  <div className="text-[10px] text-[#6C757D] mt-1">Edit freely — what you see here is what gets inserted on Approve.</div>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={discard}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs font-medium bg-white text-clay border-clay hover:bg-clay/5"
                  data-testid="our-take-discard"
                >
                  <X className="w-3.5 h-3.5" /> Discard
                </button>
                <button
                  type="button"
                  onClick={approve}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs font-medium bg-leaf text-white border-leaf hover:bg-leaf/90"
                  data-testid="our-take-approve"
                >
                  <Check className="w-3.5 h-3.5" /> Approve &amp; insert
                </button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <SecondaryButton onClick={close} data-testid="our-take-close">Close</SecondaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
